// client/src/pages/OfficeRegistryPage.js
import React, { useState, useEffect } from 'react';
import usePublicOffice from '../hooks/usePublicOffice';

const OfficeRegistryPage = () => {
    const [officeInfo, setOfficeInfo] = useState(null);
    // Florida wording from data (audit 2026-10, H8); Illinois text unchanged.
    const { isFL, jurisdiction } = usePublicOffice();
    const law = isFL ? ((jurisdiction && jurisdiction.statute) || 'Florida real estate license law') : 'the Illinois Real Estate License Act';
    const regulator = (jurisdiction && jurisdiction.regulator_short) || (isFL ? 'DBPR' : 'IDFPR');
    const virtualOffice = !isFL;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchOfficeInfo = async () => {
            try {
                const response = await fetch('/api/public/office-info');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setOfficeInfo(data);
            } catch (err) {
                setError(err.message || 'Failed to fetch office information.');
            } finally {
                setLoading(false);
            }
        };

        fetchOfficeInfo();
    }, []);

    return (
        <div className="bg-slate-50 min-h-screen">
            <div className="container mx-auto px-6 py-20">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-slate-900">Our Agents & Brokerage Registry</h1>
                    <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
                        {isFL ? 'Under' : 'As required by'} {law}, this page serves as a public registry of all licensees sponsored by Business Real Estate Corp. and provides our managing broker's contact information.
                    </p>
                </div>

                {loading && <div className="text-center"><p>Loading Registry...</p></div>}
                {error && <div className="p-4 text-center text-red-700 bg-red-100 rounded-lg">{error}</div>}
                
                {!loading && !error && officeInfo && (
                    <div className="space-y-10">
                        {/* Brokerage / Virtual Office Information */}
                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <h2 className="text-2xl font-semibold text-slate-800 mb-4">Brokerage & Virtual Office</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <p><strong>Areas Served:</strong> {officeInfo.service_area || 'Not yet specified'}</p>
                                <p><strong>Sponsoring Broker License #:</strong> {officeInfo.sponsoring_broker_license_number || 'Not on file'}</p>
                                {virtualOffice && <p><strong>Virtual Office License #:</strong> {officeInfo.virtual_office_license_number || `Registration pending with ${regulator}`}</p>}
                            </div>
                        </div>

                        {/* Managing Broker Information */}
                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <h2 className="text-2xl font-semibold text-slate-800 mb-4">
                                {officeInfo.managing_brokers && officeInfo.managing_brokers.length > 1 ? 'Designated Managing Brokers' : 'Designated Managing Broker'}
                            </h2>
                            {officeInfo.managing_brokers && officeInfo.managing_brokers.length > 0 ? (
                                <div className="space-y-4">
                                    {officeInfo.managing_brokers.map((mb, index) => (
                                        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 border-b last:border-0 last:pb-0">
                                            <p><strong>Name:</strong> {mb.name}</p>
                                            <p><strong>License #:</strong> {mb.license_number}</p>
                                            <p><strong>Email:</strong> {mb.email}</p>
                                            <p><strong>Phone:</strong> {mb.phone}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500">Managing broker information is not available.</p>
                            )}
                        </div>

                        {/* Sponsored Licensee Registry */}
                        <div className="bg-white p-8 rounded-lg shadow-md">
                             <h2 className="text-2xl font-semibold text-slate-800 mb-6">Sponsored Licensee Registry</h2>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Licensee Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">License Number</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {officeInfo.licensee_registry && officeInfo.licensee_registry.length > 0 ? (
                                            officeInfo.licensee_registry.map((licensee, index) => (
                                                <tr key={index}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{licensee.name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{licensee.license_number}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center py-10 text-slate-500">No sponsored licensees found in the registry.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OfficeRegistryPage;
